// client/src/components/configuracoes/horarios/FiltroHorarios.js - VERSÃO OTIMIZADA
import React, { useState, useMemo, useEffect } from 'react';
import { User, Box, Building } from 'lucide-react';
import { useMyAccountDetails } from '../../../hooks/useAccounts';
import { useProfissionaisQuery } from '../../../hooks/useProfissionais'; // ✅ Import da query direta
import { useRecursos } from '../../../hooks/useRecursos';
import { useUnidades } from '../../../hooks/useUnidades';

const FiltroHorarios = ({ onSelectEntidade, entidadeAtual }) => {
    const [tipoFiltro, setTipoFiltro] = useState(entidadeAtual?.tipo || 'Unidade');
    const [filtroNome, setFiltroNome] = useState('');
    
    const { data: accountDetails } = useMyAccountDetails();
    const accountId = accountDetails?._id || accountDetails?.id;

    // ✅ OTIMIZAÇÃO: Busca direta e limpa no model de profissionais
    const { 
        data: profissionaisData, 
        isLoading: profissionaisLoading 
    } = useProfissionaisQuery({ accountId, status: 'ativo', limit: 1000 }, { enabled: !!accountId });
    
    const { data: recursosDataResponse, isLoading: recursoLoading } = useRecursos(accountId, { limit: 1000 });
    const { data: unidadesDataResponse, isLoading: unidadesLoading } = useUnidades();
    
    const loading = profissionaisLoading || recursoLoading || unidadesLoading;

    useEffect(() => {
        if (entidadeAtual?.tipo) {
            setTipoFiltro(entidadeAtual.tipo);
        }
    }, [entidadeAtual]);
    
    const listaFiltrada = useMemo(() => {
        let lista = [];

        // Extração segura dos arrays
        const arrayProfissionais = Array.isArray(profissionaisData) ? profissionaisData : (profissionaisData?.data || []);
        const arrayRecursos = Array.isArray(recursosDataResponse) ? recursosDataResponse : (recursosDataResponse?.data || []);
        const arrayUnidades = Array.isArray(unidadesDataResponse) ? unidadesDataResponse : (unidadesDataResponse?.data || []);
        
        if (tipoFiltro === 'User') {
            lista = arrayProfissionais.map(prof => {
                const actualUser = prof.user || prof;
                return {
                    _id: String(actualUser._id || actualUser.id),
                    nome: actualUser.name || actualUser.nome,
                    vinculoUnidades: [] // As unidades agora vêm diretamente pelo AccountUser, se necessário preencher aqui depois
                };
            });
        } 
        else if (tipoFiltro === 'Recurso') {
            lista = arrayRecursos
                .filter(r => r && r.temAgendaPropria === true)
                .map(r => ({
                    _id: String(r._id || r.id),
                    nome: r.nome,
                    unidade: r.unidade
                }));
        } 
        else if (tipoFiltro === 'Unidade') {
            lista = arrayUnidades.map(u => ({
                _id: String(u._id || u.id),
                nome: u.nome
            }));
        }

        if (!Array.isArray(lista)) {
            lista = [];
        }

        if (!filtroNome) return lista;
        
        return lista.filter(item => {
            if (!item) return false;
            const nome = item.name || item.nome || '';
            return nome.toLowerCase().includes(filtroNome.toLowerCase());
        });
    }, [tipoFiltro, filtroNome, profissionaisData, recursosDataResponse, unidadesDataResponse]);

    const handleSelect = (e) => {
        const id = e.target.value;
        if (!id) {
            onSelectEntidade({ id: null, tipo: tipoFiltro, nome: '', obj: null });
            return;
        }
        
        const obj = listaFiltrada.find(item => item && String(item._id) === String(id));
        if (obj) {
            onSelectEntidade({ id, tipo: tipoFiltro, nome: obj.nome, obj });
        }
    };

    const handleTipoChange = (novoTipo) => {
        setTipoFiltro(novoTipo);
        setFiltroNome('');
        onSelectEntidade({ id: null, tipo: novoTipo, nome: '', obj: null });
    };

    return (
        <div className="content-panel !p-0">
            <fieldset className="form-section">
                <legend className="form-section-title">1. Escolha o que deseja gerenciar</legend>
                <div className="service-category-filters mb-4">
                    <button type="button" className={`category-button ${tipoFiltro === 'Unidade' ? 'is-active' : ''}`} onClick={() => handleTipoChange('Unidade')}>
                        <Building className="icon" size={16} /> Horários da Empresa
                    </button>
                    <button type="button" className={`category-button ${tipoFiltro === 'User' ? 'is-active' : ''}`} onClick={() => handleTipoChange('User')}>
                        <User className="icon" size={16} /> Profissionais
                    </button>
                    <button type="button" className={`category-button ${tipoFiltro === 'Recurso' ? 'is-active' : ''}`} onClick={() => handleTipoChange('Recurso')}>
                        <Box className="icon" size={16} /> Recursos com Agenda
                    </button>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">2. Selecione a Entidade</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label htmlFor="filtroNome">Buscar por Nome</label>
                        <input 
                            type="text" 
                            id="filtroNome" 
                            className="form-input" 
                            placeholder={`Buscar em ${tipoFiltro === 'User' ? 'profissionais' : tipoFiltro === 'Recurso' ? 'recursos' : 'unidades'}...`}
                            value={filtroNome} 
                            onChange={e => setFiltroNome(e.target.value)} 
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="entidadeAgendavel">
                            {tipoFiltro === 'Unidade' ? 'Selecione a Unidade' : 'Selecione para ver a agenda'}
                        </label>
                        <select 
                            id="entidadeAgendavel" 
                            value={entidadeAtual?.id || ''}
                            onChange={handleSelect} 
                            className="form-input" 
                            disabled={loading}
                        >
                            <option value="">-- Selecione --</option>
                            {listaFiltrada.map(item => (
                                <option key={item._id} value={item._id}>
                                    {item.nome}
                                </option>
                            ))}
                        </select>
                        {loading && <div className="form-hint">Carregando...</div>}
                        {!loading && listaFiltrada.length === 0 && (
                            <div className="form-hint text-warning">
                                Nenhuma opção encontrada para esta aba.
                            </div>
                        )}
                    </div>
                </div>
            </fieldset>
        </div>
    );
};

export default FiltroHorarios;